import { useEffect, useState } from 'react';
import { getStaffPass } from '@generated/hooks/staffPass';
import { Printer, Loader2 } from 'lucide-react';
import { getClassifiedReport } from '@generated/server/classifieds';
import { upcomingMonths } from '@generated/components/pricing';

type Ad = Awaited<ReturnType<typeof getClassifiedReport>>['ads'][number];
const PAPERS = ['All', 'Red Hook Star-Revue', 'The Village Star-Revue'];

export function ClassifiedReport() {
  const months = upcomingMonths(4).map((m) => m.label);
  const [issue, setIssue] = useState(months[0] ?? '');
  const [paper, setPaper] = useState('All');
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let on = true;
    setLoading(true); setError(null);
    getClassifiedReport({ data: { pass: getStaffPass(), issueMonth: issue, publication: paper } })
      .then((r) => { if (on) setAds(r?.ads ?? []); })
      .catch((e) => { console.error(e); if (on) setError('Could not load classifieds.'); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [issue, paper]);

  const groups = new Map<string, Ad[]>();
  for (const a of ads) {
    const k = a.data.category.trim().toUpperCase() || 'MISCELLANEOUS';
    groups.set(k, [...(groups.get(k) ?? []), a]);
  }
  const cats = [...groups.keys()].sort();

  return (
    <section id="classified-report" className="mt-12">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-foreground pb-2 print:hidden">
        <h2 className="font-[family-name:var(--font-heading)] text-3xl font-semibold">Classifieds report</h2>
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Issue" value={issue} onChange={(e) => setIssue(e.target.value)} className="h-11 border border-input bg-background px-2 text-sm">{months.map((m) => <option key={m}>{m}</option>)}</select>
          <select aria-label="Paper" value={paper} onChange={(e) => setPaper(e.target.value)} className="h-11 border border-input bg-background px-2 text-sm">{PAPERS.map((p) => <option key={p}>{p}</option>)}</select>
          <button onClick={() => window.print()} className="inline-flex min-h-11 items-center gap-2 bg-foreground px-4 text-sm font-semibold text-background"><Printer className="h-4 w-4" />Print report</button>
        </div>
      </div>
      <div className="mt-6">
        <div className="text-center">
          <div className="font-[family-name:var(--font-heading)] text-3xl font-extrabold">Classifieds</div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{paper === 'All' ? 'Star-Revue' : paper} · {issue} · {ads.length} ad{ads.length === 1 ? '' : 's'}</div>
        </div>
        {loading && <div className="mt-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>}
        {error && <p className="mt-4 text-sm text-destructive">{error}</p>}
        {!loading && !error && ads.length === 0 && <p className="mt-6 text-center text-sm text-muted-foreground">No classifieds for this issue yet.</p>}
        <div className="mt-6 columns-1 gap-8 sm:columns-2 lg:columns-3 print:columns-3">
          {cats.map((c) => (
            <div key={c} className="mb-5 break-inside-avoid">
              <div className="border-b-2 border-foreground pb-1 font-[family-name:var(--font-heading)] text-sm font-bold tracking-wider">{c}</div>
              {(groups.get(c) ?? []).map((a) => (
                <div key={a.id} className="border-b border-border py-2 text-sm leading-snug">
                  <p>{a.data.adText}</p>
                  <p className="mt-1 text-[11px] text-muted-foreground print:hidden">{a.data.name} · {a.data.email}{a.data.phone ? ` · ${a.data.phone}` : ''} · {a.data.publication} · paid by {a.data.paymentMethod}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
